import React from 'react'
import { PageShell } from './PageShell'
import { LogVisitModal } from '../components/LogVisitModal'
import { useDashboard } from '../DashboardContext'
import { formatDateShort, initialsFromName } from '../utils'
import { markNoShow } from '../../../lib/nudgeApi'
import { SkeletonTableRows } from '../../../components/ui/Skeleton'
import type { NudgeQueueSummary } from '../../../types/api'
import type { NudgeStatus } from '../../../types/enums'

type Tab = 'awaiting' | 'returned' | 'no_show'

const TAB_LABEL: Record<Tab, string> = {
  awaiting: 'Awaiting confirmation',
  returned: 'Confirmed returns',
  no_show:  'No-show',
}

// Pick the most recently-updated row per contact for the given status.
function rowsForStatus(queueRows: NudgeQueueSummary[], status: NudgeStatus): NudgeQueueSummary[] {
  const byContact = new Map<string, NudgeQueueSummary>()
  for (const row of queueRows) {
    if (row.status !== status) continue
    const existing = byContact.get(row.contactId)
    if (!existing || new Date(row.updatedAt) > new Date(existing.updatedAt)) {
      byContact.set(row.contactId, row)
    }
  }
  return Array.from(byContact.values())
}

export function RecoveredPage({ isActive }: { isActive: boolean }) {
  const { queueRows, queueLoading, refreshQueue } = useDashboard()

  const [tab, setTab] = React.useState<Tab>('awaiting')
  const [logVisitTarget, setLogVisitTarget] = React.useState<{
    contactId: string
    contactName: string
  } | null>(null)
  const [noShowBusy, setNoShowBusy] = React.useState<string | null>(null)

  async function handleMarkNoShow(rowId: string) {
    setNoShowBusy(rowId)
    try {
      await markNoShow(rowId)
      refreshQueue()
    } finally {
      setNoShowBusy(null)
    }
  }

  // Awaiting confirmation (RECOVERED) — sort by commitment urgency: overdue first,
  // soonest next, then plain RECOVERED by recency.
  const awaitingRows = React.useMemo(() => {
    return rowsForStatus(queueRows, 'RECOVERED').sort((a, b) => {
      const aCommitted = a.committedByDate ? new Date(a.committedByDate).getTime() : null
      const bCommitted = b.committedByDate ? new Date(b.committedByDate).getTime() : null
      if (aCommitted !== null && bCommitted !== null) return aCommitted - bCommitted
      if (aCommitted !== null) return -1
      if (bCommitted !== null) return 1
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    })
  }, [queueRows])

  // Confirmed returns (RETURNED) — most recent first.
  const returnedRows = React.useMemo(() => {
    return rowsForStatus(queueRows, 'RETURNED').sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
  }, [queueRows])

  // No-show (NO_SHOW) — most recent first.
  const noShowRows = React.useMemo(() => {
    return rowsForStatus(queueRows, 'NO_SHOW').sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
  }, [queueRows])

  const counts: Record<Tab, number> = {
    awaiting: awaitingRows.length,
    returned: returnedRows.length,
    no_show:  noShowRows.length,
  }

  const visibleRows: NudgeQueueSummary[] =
    tab === 'awaiting' ? awaitingRows :
    tab === 'returned' ? returnedRows :
    noShowRows

  const todayStart = React.useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }, [])

  const headerSub: Record<Tab, string> = {
    awaiting: 'These customers came back thanks to a nudge. Confirm their visit status below.',
    returned: 'Customers whose return was confirmed by a logged visit.',
    no_show:  'Customers who confirmed they\'d return but did not visit within the window.',
  }

  const emptyLabel: Record<Tab, string> = {
    awaiting: 'No recovered contacts awaiting confirmation.',
    returned: 'No confirmed returns yet — keep nudging.',
    no_show:  'No no-shows to report.',
  }

  return (
    <>
      <PageShell id="page-recovered" isActive={isActive}>
        <div className="content">
          <div className="lapsed-header-bar">
            <div>
              <div className="lh-num">{counts[tab]}</div>
              <div
                style={{
                  fontSize: 10,
                  fontFamily: "'DM Mono',monospace",
                  color: 'var(--text3)',
                  marginTop: 2,
                  textTransform: 'uppercase',
                }}
              >
                {tab === 'awaiting' ? 'AWAITING ACTION'
                  : tab === 'returned' ? 'CONFIRMED'
                  : 'NO-SHOWS'}
              </div>
            </div>
            <div className="lh-divider" />
            <div>
              <div className="lh-label">Recovered Contacts</div>
              <div className="lh-sub">{headerSub[tab]}</div>
            </div>
          </div>

          <div
            role="tablist"
            aria-label="Recovered contacts view"
            style={{
              display: 'flex',
              gap: 4,
              marginBottom: 12,
              borderBottom: '1px solid var(--border)',
            }}
          >
            {(['awaiting', 'returned', 'no_show'] as const).map(key => {
              const active = tab === key
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={() => setTab(key)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '10px 14px',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: active ? 'var(--text1)' : 'var(--text2)',
                    cursor: 'pointer',
                    borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
                    marginBottom: -1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {TAB_LABEL[key]}
                  <span
                    style={{
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 11,
                      padding: '1px 7px',
                      borderRadius: 8,
                      background: active ? 'var(--orange-bg)' : 'var(--bg2)',
                      color: active ? 'var(--orange)' : 'var(--text3)',
                    }}
                  >
                    {counts[key]}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="panel" aria-busy={queueLoading} aria-label={`Recovered contacts — ${TAB_LABEL[tab]}`}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Nudge #</th>
                  {tab === 'awaiting' && <th>Recovered On</th>}
                  {tab === 'awaiting' && <th>Expected By</th>}
                  {tab === 'returned' && <th>Returned On</th>}
                  {tab === 'no_show'  && <th>Marked On</th>}
                  {tab === 'no_show'  && <th>Was Expected By</th>}
                  {tab === 'awaiting' && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {queueLoading ? (
                  <SkeletonTableRows cols={tab === 'awaiting' ? 5 : tab === 'returned' ? 3 : 4} rows={5} />
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={tab === 'awaiting' ? 5 : tab === 'returned' ? 3 : 4}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 8,
                          padding: '48px 16px',
                          color: 'var(--text2)',
                          fontSize: 13,
                        }}
                      >
                        <svg
                          width="36"
                          height="36"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ opacity: 0.35 }}
                          aria-hidden="true"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span>{emptyLabel[tab]}</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(row => (
                    <tr key={row.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="avatar-circle"
                            style={{ background: 'var(--orange-bg)', color: 'var(--orange)' }}
                          >
                            {initialsFromName(row.contactName)}
                          </div>
                          <div>
                            <div className="cust-name">{row.contactName}</div>
                            <div className="cust-sub">{row.contactEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="pill pill-pending">Nudge #{row.sequenceNumber}</span>
                      </td>

                      {tab === 'awaiting' && (
                        <>
                          <td
                            style={{
                              fontFamily: "'DM Mono',monospace",
                              fontSize: 12,
                              color: 'var(--text2)',
                            }}
                          >
                            {formatDateShort(row.updatedAt)}
                          </td>
                          <td
                            style={{
                              fontFamily: "'DM Mono',monospace",
                              fontSize: 12,
                            }}
                          >
                            {row.committedByDate ? (
                              (() => {
                                const expected = new Date(row.committedByDate).getTime()
                                const overdue = expected < todayStart
                                return (
                                  <span
                                    style={{ color: overdue ? 'var(--red)' : 'var(--text2)' }}
                                    title={overdue ? 'Past the date the customer committed to' : undefined}
                                  >
                                    {formatDateShort(row.committedByDate)}
                                    {overdue ? ' · overdue' : ''}
                                  </span>
                                )
                              })()
                            ) : (
                              <span style={{ color: 'var(--text3)' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="btn-primary"
                                type="button"
                                style={{ fontSize: 12, padding: '6px 14px' }}
                                onClick={() =>
                                  setLogVisitTarget({
                                    contactId: row.contactId,
                                    contactName: row.contactName,
                                  })
                                }
                              >
                                Log Visit
                              </button>
                              <button
                                className="btn-secondary"
                                type="button"
                                style={{ fontSize: 12, padding: '6px 14px' }}
                                disabled={noShowBusy === row.id}
                                onClick={() => void handleMarkNoShow(row.id)}
                              >
                                {noShowBusy === row.id ? 'Marking…' : 'No Show'}
                              </button>
                            </div>
                          </td>
                        </>
                      )}

                      {tab === 'returned' && (
                        <td
                          style={{
                            fontFamily: "'DM Mono',monospace",
                            fontSize: 12,
                            color: 'var(--text2)',
                          }}
                        >
                          {formatDateShort(row.updatedAt)}
                        </td>
                      )}

                      {tab === 'no_show' && (
                        <>
                          <td
                            style={{
                              fontFamily: "'DM Mono',monospace",
                              fontSize: 12,
                              color: 'var(--text2)',
                            }}
                          >
                            {formatDateShort(row.updatedAt)}
                          </td>
                          <td
                            style={{
                              fontFamily: "'DM Mono',monospace",
                              fontSize: 12,
                              color: 'var(--text3)',
                            }}
                          >
                            {row.committedByDate ? formatDateShort(row.committedByDate) : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </PageShell>

      <LogVisitModal
        open={logVisitTarget !== null}
        contactId={logVisitTarget?.contactId ?? ''}
        contactName={logVisitTarget?.contactName ?? ''}
        onClose={() => setLogVisitTarget(null)}
        onSaved={() => {
          setLogVisitTarget(null)
          refreshQueue()
        }}
      />
    </>
  )
}
